import React from "react";
import { useParams, useRouteMatch } from "react-router-dom";
import { ButtonProps, CommandBarButton, ConfirmDialog, useDisclosure, useNotifications } from "sprint-common-web";

import { SprintAccessRight } from "../../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../../core/security/useAccessVerifier";
import { getConformingAssetDetailsHref } from "../../utils/getConformingAssetDetailsHref";
import { ConformingAssetListingItem } from "../ConformingAssetItem";
import { ConformingAssetList } from "../ConformingAssetList";
import { createConformingAssetsSectionColumns } from "../createConformingAssetsSectionColumns";
import { useDeprecateAssetGroup } from "./useDeprecateAssetGroup";

export type DeprecateAssetGroupDialogButtonProps = Pick<ButtonProps, "disabled"> & {
    isCloud: boolean;
    conformingAssets: ConformingAssetListingItem[];
    contentVersionIds: string[];
    isLoading: boolean;
};

export const DeprecateAssetGroupDialogButton: React.FC<DeprecateAssetGroupDialogButtonProps> = ({
    isCloud,
    disabled,
    isLoading,
    conformingAssets,
    contentVersionIds
}: DeprecateAssetGroupDialogButtonProps) => {
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { success, error } = useNotifications();
    const { isAuthorized } = useAccessVerifier();

    const { params } = useRouteMatch<{ assetType: string }>();
    const { assetId } = useParams<{ assetId: string }>();
    const { deprecateOnPremAssetGroup, deprecateCloudAssetGroup } = useDeprecateAssetGroup({
        invalidationKey: assetId
    });
    const columns = createConformingAssetsSectionColumns({
        getHref: (item) => getConformingAssetDetailsHref(item.id, isCloud, params.assetType),
        getTooltipText: (item) => item.path,
        isAnnotated: (item) => item.id === assetId && "current",
        isDisabled: (item) => item.id === assetId
    });

    const callOnConfirm = async () => {
        try {
            if (isCloud) {
                await deprecateCloudAssetGroup.mutateAsync({ contentVersionIds });
            } else {
                await deprecateOnPremAssetGroup.mutateAsync({ contentVersionIds });
            }

            success("All assets have been deprecated successfully.");
        } catch {
            error("Failed to deprecate assets.");
        } finally {
            onClose();
        }
    };

    return (
        <>
            <CommandBarButton
                styles={{ root: { background: "transparent" } }}
                disabled={disabled || !isAuthorized([SprintAccessRight.AssetsReadWrite])}
                onClick={onOpen}
                icon="PageRemove"
                text="Deprecate all"
            />
            <ConfirmDialog
                title={`Move to 'Deprecated'`}
                text={`You are about to deprecate current asset with all conforming audio, subtitle and video files listed below:`}
                confirm={{ onClick: callOnConfirm }}
                cancel={{ onClick: onClose }}
                hidden={!isOpen}
                width="50%"
            >
                <ConformingAssetList items={conformingAssets} columns={columns} isLoading={isLoading} />
            </ConfirmDialog>
        </>
    );
};
